import { useMemo, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types/customer";
import { IssueRow } from "@/components/letters/issue-row";
import { RowsSkeleton } from "@/components/properties/rows-skeleton";
import { LettersTabs } from "@/components/shell/letters-tabs";
import {
  Card,
  EmptyState,
  FilterBar,
  FormField,
  inputClass,
  LoadError,
  PageTitle,
  RowList,
  Select,
} from "@/components/ui";
import { useActiveAccountId, useActiveMembership } from "@/hooks/use-customer-context";
import { useCustomerViolations } from "@/hooks/use-customer-violations";
import { useUrlSearchText } from "@/hooks/use-url-search-text";
import { filterIssues, openIssueSummary, sortIssues } from "@/lib/issue-list";
import { ISSUE_STATUS_FILTERS, validateIssuesSearch } from "@/lib/letters-search";

type CustomerViolation = components["schemas"]["CustomerViolation"];

export const Route = createFileRoute("/_customer/letters/issues/")({
  validateSearch: validateIssuesSearch,
  component: IssuesPage,
});

const NO_VIOLATIONS: readonly CustomerViolation[] = [];

/**
 * Compliance issues: the active account's issues, open ones first by deadline,
 * narrowed by the search text and the status in the URL. The issues read is
 * drained whole, so the narrowing and the order are applied here; the tab
 * counts every open issue whatever the filters show.
 */
function IssuesPage() {
  const { t } = useTranslation("customer");
  const navigate = useNavigate();
  const { q, status } = Route.useSearch();
  const accountId = useActiveAccountId();
  const org = useActiveMembership()?.org_name ?? "";
  const read = useCustomerViolations(accountId ? { account_id: accountId } : {});
  const items = read.data?.items ?? NO_VIOLATIONS;

  // Every write passes through the validator, so the URL holds what the page
  // reads back: an empty choice is the param's absence.
  const onSearch = (next: Record<string, unknown>, options?: { replace?: boolean }) =>
    void navigate({ to: "/letters/issues", search: validateIssuesSearch(next), ...options });

  const [draft, setDraft] = useUrlSearchText(q, (next) =>
    onSearch({ q: next, status }, { replace: true }),
  );

  const openCount = openIssueSummary(items).count;
  const visible = useMemo(
    () => sortIssues(filterIssues(items, { q, status }, t)),
    [items, q, status, t],
  );

  const filtered = q !== undefined || status !== undefined;
  const reset = () => {
    setDraft("");
    onSearch({});
  };

  let body: ReactNode;
  if (read.isError) {
    body = (
      <LoadError
        title={t("issues.error")}
        body={t("loadError.body")}
        onRetry={() => void read.refetch()}
      />
    );
  } else if (read.isPending) {
    body = <RowsSkeleton />;
  } else if (visible.length > 0) {
    body = (
      <RowList>
        {visible.map((violation) => (
          <IssueRow key={violation.id} violation={violation} />
        ))}
      </RowList>
    );
  } else if (filtered) {
    body = (
      <Card>
        <EmptyState
          variant="inline"
          title={t("issues.noMatch")}
          actionLabel={t("filters.reset")}
          onAction={reset}
        />
      </Card>
    );
  } else {
    body = (
      <Card>
        <p className="text-sm text-body">{t("issues.empty", { org })}</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5 lg:gap-6">
      <PageTitle title={t("letters.title")} sub={t("letters.sub", { org })}>
        <LettersTabs value="issues" openIssues={openCount} />
      </PageTitle>
      <div className="flex flex-col gap-4">
        <FilterBar onReset={reset} canReset={filtered || draft.trim() !== ""}>
          <div className="sm:min-w-64 sm:flex-1">
            <FormField label={t("issues.search")}>
              <input
                type="search"
                className={inputClass}
                value={draft}
                placeholder={t("issues.searchPlaceholder")}
                onChange={(event) => setDraft(event.target.value)}
              />
            </FormField>
          </div>
          <div className="sm:w-56">
            <FormField label={t("issues.status")}>
              <Select
                value={status ?? ""}
                options={[
                  { value: "", label: t("issues.statusAll") },
                  ...ISSUE_STATUS_FILTERS.map((value) => ({
                    value,
                    label: t(`violations.status.${value}`),
                  })),
                ]}
                onChange={(event) => onSearch({ q, status: event.target.value })}
              />
            </FormField>
          </div>
        </FilterBar>
        {body}
      </div>
    </div>
  );
}
